// ============================================================================
// OOM SERVICE
// Handles Order of Merit API calls
// ============================================================================

const API_BASE_URL = 'https://api.fsgu.co.za';

/**
 * Get OOM standings for a category and year
 */
export const getStandings = async (category, year) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/oom-standings.php?category=${encodeURIComponent(category)}&year=${year}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }
    );

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to fetch standings');
    }

    return data;
  } catch (error) {
    console.error('Error fetching standings:', error);
    throw error;
  }
};

/**
 * Save a single OOM score (inline edit)
 */
export const saveScore = async (scoreData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/oom-scores.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(scoreData),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to save score');
    }

    return data;
  } catch (error) {
    console.error('Error saving score:', error);
    throw error;
  }
};

/**
 * Update a single OOM score by ID
 */
export const updateScore = async (id, points) => {
  try {
    const response = await fetch(`${API_BASE_URL}/oom-scores.php`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, points }),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to update score');
    }

    return data;
  } catch (error) {
    console.error('Error updating score:', error);
    throw error;
  }
};

/**
 * Save a single standing (legacy - used by AdminOOM for player info edits)
 */
export const saveStanding = async (standing, adminId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/save-oom-standing.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...standing, updated_by: adminId }),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to save standing');
    }

    return data;
  } catch (error) {
    console.error('Error saving standing:', error);
    throw error;
  }
};

/**
 * Delete a single standing
 */
export const deleteStanding = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/delete-oom-standing.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id }),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to delete standing');
    }

    return data;
  } catch (error) {
    console.error('Error deleting standing:', error);
    throw error;
  }
};

/**
 * Recalculate OOM positions
 */
export const recalculatePositions = async (category, year) => {
  try {
    const response = await fetch(`${API_BASE_URL}/recalculate-oom-positions.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ category, year }),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to recalculate positions');
    }

    return data;
  } catch (error) {
    console.error('Error recalculating positions:', error);
    throw error;
  }
};

/**
 * Save/update a player (for admin inline editing)
 */
export const savePlayer = async (playerData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/oom-players.php`, {
      method: playerData.id ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(playerData),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to save player');
    }

    return data;
  } catch (error) {
    console.error('Error saving player:', error);
    throw error;
  }
};

/**
 * Delete a player
 */
export const deletePlayer = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/oom-players.php?id=${id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to delete player');
    }

    return data;
  } catch (error) {
    console.error('Error deleting player:', error);
    throw error;
  }
};

/**
 * OOM Categories list
 */
export const OOM_CATEGORIES = [
  { value: 'Mens_Open', label: "Men's Open" },
  { value: 'Mens_Mid_Am', label: "Men's Mid-Am" },
  { value: 'Mens_Senior', label: "Men's Senior" },
  { value: 'Mens_Super_Senior', label: "Men's Super Senior" },
  { value: 'Ladies_Open', label: "Ladies Open" },
  { value: 'Ladies_Mid_Am', label: "Ladies Mid-Am" },
  { value: 'Ladies_Senior', label: "Ladies Senior" },
  { value: 'Ladies_Super_Senior', label: "Ladies Super Senior" },
  { value: 'Junior_Boys', label: 'Junior Boys' },
  { value: 'Junior_Girls', label: 'Junior Girls' },
  { value: 'Mixed', label: 'Mixed' },
  { value: 'Country_Districts', label: 'Country Districts' },
];

export { API_BASE_URL };